package com.arena_web.ui.sections.profile.edit

import androidx.compose.runtime.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.MatchedText
import com.arena_web.ui.components.widgets.SuggestionMuted
import com.arena_web.ui.components.widgets.SuggestionPrimary
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.theme.AppColors
import com.arena_web.utils.CityResult
import com.arena_web.utils.LeafletMap
import com.arena_web.utils.reverseGeocode
import com.arena_web.utils.cachedUserCoordinates
import com.arena_web.utils.searchCities
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

data class PersonalInfoFormState(
    val firstName: String = "",
    val lastName: String = "",
    val dateOfBirth: String = "",        // DD.MM.YYYY for UI; converted on save
    val city: String = "",
    val languages: List<String> = emptyList(),
    val bio: String = ""
) {
    fun isValid(): Boolean =
        firstName.isNotBlank() &&
            dateOfBirth.length == 10 &&
            city.isNotBlank() &&
            languages.isNotEmpty()
}

private const val BIO_MAX = 300

@Composable
fun PersonalInfoEditModal(
    isOpen: Boolean,
    initial: PersonalInfoFormState,
    onClose: () -> Unit,
    onSave: (PersonalInfoFormState) -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var state by remember(initial) { mutableStateOf(initial.copy(dateOfBirth = maskDob(initial.dateOfBirth))) }

    // Mobile → bottom sheet with a single-column form (Figma 8186:195575 /
    // 8171:173208); desktop → centred popup with the 2×2 field grid.
    if (isMobile()) {
        ProfileEditSheet(
            title = t.profilePersonalEditTitle,
            onClose = onClose,
            onCancel = onClose,
            onSave = { onSave(state.commit()) },
            saveEnabled = state.isValid(),
        ) {
            PersonalInfoFormBody(state = state, onStateChange = { state = it }, singleColumn = true)
        }
    } else {
        ProfileEditShell(
            isOpen = true,
            title = t.profilePersonalEditTitle,
            description = t.profilePersonalEditDescription,
            onClose = onClose,
            onCancel = onClose,
            onSave = { onSave(state.commit()) },
            saveEnabled = state.isValid(),
        ) {
            PersonalInfoFormBody(state = state, onStateChange = { state = it })
        }
    }
}

internal fun PersonalInfoFormState.commit(): PersonalInfoFormState = copy(
    firstName = firstName.trim(),
    lastName = lastName.trim(),
    dateOfBirth = dateOfBirth.trim(),
    city = city.trim(),
    bio = bio.trim(),
)

/**
 * The visual body of the Personal Info form — text fields, language picker,
 * About me — extracted from [PersonalInfoEditModal] so the
 * Complete-Profile wizard can embed the same form in its own shell with a
 * Next button instead of Save.
 *
 * Stateless: the caller owns [state] and gets every edit through
 * [onStateChange] so it can persist on its own commit triggers.
 */
@Composable
internal fun PersonalInfoFormBody(
    state: PersonalInfoFormState,
    onStateChange: (PersonalInfoFormState) -> Unit,
    singleColumn: Boolean = false,
) {
    val t = LocalAppStrings.current
    if (singleColumn) {
        PersonalInfoFormBodySingleColumn(state = state, onStateChange = onStateChange)
        return
    }
    // Wrapper with symmetric horizontal padding inside the modal body —
    // gives the form container a light, equal inset from both edges.
    // (Earlier attempt with `margin-left/right` on the form Box itself
    // didn't override `fillMaxWidth`'s `width: 100%`, so the right side
    // looked bigger than the left.)
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding-left", s(16))
                property("padding-right", s(16))
                property("box-sizing", "border-box")
            }
    ) {
    // Form container — rounded 18, padded 14x16, with gradient outline overlay
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(18)
            .styleModifier {
                property("position", "relative")
            }
    ) {
        GradientBorderRing(radiusPx = 18)
        // Off-screen honeypot inputs — Chrome's autofill heuristic prefers
        // earlier fields with matching `autocomplete` tokens, so the saved
        // profile data gets poured into these decoys instead of our real
        // First/Last name / DOB / City inputs (all of which carry
        // `autocomplete="off"`). Display:none would not work — Chrome
        // ignores hidden inputs for autofill — so the trap stays in the
        // layout but is positioned far off-screen.
        AutofillTrap()
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(24)
                .styleModifier {
                    property("padding", "${s(16)} ${s(14)}")
                }
        ) {
        // Section 1: 2x2 grid (First/Last name + DOB/City) with bottom divider
        Column(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(24)
                    .sPaddingBottom(18)
            ) {
                Row(modifier = Modifier.fillMaxWidth().sGap(12)) {
                    LabeledTextField(
                        label = t.profilePersonalEditFirstNameLabel,
                        value = state.firstName,
                        placeholder = "",
                        onChange = { onStateChange(state.copy(firstName = it)) },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0") },
                        required = true,
                    )
                    LabeledTextField(
                        label = t.profilePersonalEditLastNameLabel,
                        value = state.lastName,
                        placeholder = "",
                        onChange = { onStateChange(state.copy(lastName = it)) },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0") },
                    )
                }
                Row(modifier = Modifier.fillMaxWidth().sGap(12)) {
                    DateOfBirthField(
                        value = state.dateOfBirth,
                        onChange = { onStateChange(state.copy(dateOfBirth = maskDob(it))) },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0") }
                    )
                    CityAutocompleteField(
                        value = state.city,
                        onChange = { onStateChange(state.copy(city = it)) },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0") }
                    )
                }
            }
            SectionGradientDivider()
        }

        // Section 2: Languages
        Column(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(6)
                    .sPaddingBottom(18)
            ) {
                LanguageAutocompleteField(
                    selected = state.languages,
                    onAdd = { lang ->
                        if (state.languages.none { it.equals(lang, ignoreCase = true) }) {
                            onStateChange(state.copy(languages = state.languages + lang))
                        }
                    },
                    onRemove = { lang ->
                        onStateChange(state.copy(
                            languages = state.languages.filterNot { it.equals(lang, ignoreCase = true) }
                        ))
                    }
                )
            }
            SectionGradientDivider()
        }

        // Section 3: About me + counter
        Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
            Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                FieldLabel(t.profilePersonalEditAboutMeLabel)
                AboutMeTextArea(
                    value = state.bio,
                    onChange = { onStateChange(state.copy(bio = it.take(BIO_MAX))) }
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("justify-content", "flex-end") }
            ) {
                SpanText(
                    "${state.bio.length} / $BIO_MAX",
                    modifier = Modifier
                        .sFontSize(10)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(12)
                        .sLetterSpacing(0.1)
                        .color(LocalSportColors.current.metricsPrimary)
                )
            }
        }
        }   // inner Column (form content)
    }       // form container Box
    }       // padded wrapper Box
}

/**
 * Single-column mobile layout (Figma 8186:195575 / 8171:173208) — every field
 * is a full-width pill stacked vertically with no outer container chrome, the
 * way the phone sheet draws it. Reuses the same field composables as the
 * desktop grid so validation and autocomplete behaviour stay identical.
 */
@Composable
private fun PersonalInfoFormBodySingleColumn(
    state: PersonalInfoFormState,
    onStateChange: (PersonalInfoFormState) -> Unit,
) {
    val t = LocalAppStrings.current
    Box(modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
        AutofillTrap()
        Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
            LabeledTextField(
                label = t.profilePersonalEditFirstNameLabel,
                value = state.firstName,
                placeholder = "",
                onChange = { onStateChange(state.copy(firstName = it)) },
                modifier = Modifier.fillMaxWidth(),
                required = true,
            )
            LabeledTextField(
                label = t.profilePersonalEditLastNameLabel,
                value = state.lastName,
                placeholder = "",
                onChange = { onStateChange(state.copy(lastName = it)) },
                modifier = Modifier.fillMaxWidth(),
            )
            DateOfBirthField(
                value = state.dateOfBirth,
                onChange = { onStateChange(state.copy(dateOfBirth = maskDob(it))) },
                modifier = Modifier.fillMaxWidth()
            )
            CityAutocompleteField(
                value = state.city,
                onChange = { onStateChange(state.copy(city = it)) },
                modifier = Modifier.fillMaxWidth()
            )
            LanguageAutocompleteField(
                selected = state.languages,
                onAdd = { lang ->
                    if (state.languages.none { it.equals(lang, ignoreCase = true) }) {
                        onStateChange(state.copy(languages = state.languages + lang))
                    }
                },
                onRemove = { lang ->
                    onStateChange(state.copy(
                        languages = state.languages.filterNot { it.equals(lang, ignoreCase = true) }
                    ))
                }
            )
            Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                    FieldLabel(t.profilePersonalEditAboutMeLabel)
                    AboutMeTextArea(
                        value = state.bio,
                        onChange = { onStateChange(state.copy(bio = it.take(BIO_MAX))) }
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth().styleModifier { property("justify-content", "flex-end") }
                ) {
                    SpanText(
                        "${state.bio.length} / $BIO_MAX",
                        modifier = Modifier
                            .sFontSize(10)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(12)
                            .sLetterSpacing(0.1)
                            .color(LocalSportColors.current.metricsPrimary)
                    )
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(label: String, required: Boolean = false) {
    if (required) {
        Row(
            modifier = Modifier.sPaddingLeft(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(Color("#818D9C"))
            )
            SpanText(
                " *",
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(Color("#FF4D4F"))
            )
        }
    } else {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .sPaddingLeft(4)
                .color(Color("#818D9C"))
        )
    }
}

@Composable
private fun LabeledTextField(
    label: String,
    value: String,
    placeholder: String,
    onChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    required: Boolean = false,
) {
    Column(modifier = modifier.sGap(6)) {
        FieldLabel(label, required = required)
        FieldInput(value = value, placeholder = placeholder, onChange = onChange)
    }
}

// =============================================================================
// Suppresses Chrome's autofill *suggestion dropdown* on a per-field basis:
// wrapping the real `<input>` in a `<form autocomplete="off">` is the only
// thing Chrome reliably honours for heuristically-detected name/address
// fields. `display:contents` keeps the form invisible to layout, and
// `onsubmit="return false"` makes Enter inside the input a no-op.
// =============================================================================
@Composable
private fun NoAutofillFormWrap(content: @Composable () -> Unit) {
    Form(attrs = {
        attr("autocomplete", "off")
        attr("onsubmit", "return false")
        attr("role", "presentation")
        style { property("display", "contents") }
    }) {
        content()
    }
}

// =============================================================================
// Autofill honeypot — see comment at the call site for why these decoy inputs
// are necessary. Each carries a Chrome-recognised `autocomplete` token so the
// browser pours its saved profile data into the trap, leaving the real fields
// (all marked `autocomplete="off"`) untouched.
// =============================================================================
@Composable
private fun AutofillTrap() {
    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .styleModifier {
                property("left", "-9999px")
                property("top", "-9999px")
                property("width", "1px")
                property("height", "1px")
                property("overflow", "hidden")
                property("opacity", "0")
                property("pointer-events", "none")
            }
    ) {
        val decoys = listOf(
            "username", "current-password", "given-name", "family-name",
            "email", "tel", "address-level2", "bday",
        )
        decoys.forEach { token ->
            Input(
                type = if (token == "current-password") {
                    org.jetbrains.compose.web.attributes.InputType.Password
                } else org.jetbrains.compose.web.attributes.InputType.Text,
                attrs = Modifier.toAttrs {
                    attr("autocomplete", token)
                    attr("name", "honeypot-$token")
                    attr("tabindex", "-1")
                    attr("aria-hidden", "true")
                }
            )
        }
    }
}

@Composable
private fun FieldInput(
    value: String,
    placeholder: String,
    onChange: (String) -> Unit,
    onSubmit: (() -> Unit)? = null,
    inputMode: String? = null,
) {
    // Chrome ignores `autocomplete="off"` for fields it heuristically
    // identifies (First name, DOB, etc.). Bidirectional readonly trick:
    // the input is `readonly` whenever it isn't focused, so every time it
    // gains focus Chrome's autofill check sees a readonly field and skips.
    // We drop the attribute on focusin (so the user can type) and reattach
    // it on focusout (so the next focus is gated again).
    var fieldFocused by remember { mutableStateOf(false) }
    val mobile = isMobile()
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(44)
            .sBorderRadius(28)
            .styleModifier {
                property("position", "relative")
                property("background", EDIT_FIELD_BG)
            }
    ) {
        GradientBorderRing(radiusPx = 28)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "0 ${s(12)}")
                    property("display", "flex")
                    property("align-items", "center")
                }
        ) {
            NoAutofillFormWrap {
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = Modifier.toAttrs {
                value(value)
                attr("placeholder", placeholder)
                if (inputMode != null) attr("inputmode", inputMode)
                // Anti-autofill recipe — see comment above for why `readonly`
                // is the only one Chrome actually obeys.
                attr("autocomplete", "off")
                attr("autocorrect", "off")
                attr("autocapitalize", "off")
                attr("spellcheck", "false")
                attr("data-form-type", "other")
                attr("data-lpignore", "true")
                attr("data-1p-ignore", "true")
                if (!fieldFocused && !mobile) attr("readonly", "true")
                onFocusIn { fieldFocused = true }
                onFocusOut { fieldFocused = false }
                onInput { onChange(it.value) }
                if (onSubmit != null) {
                    onKeyDown { ev ->
                        if (ev.key == "Enter") {
                            ev.preventDefault()
                            onSubmit()
                        }
                    }
                }
                style {
                    property("width", "100%")
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", "#BED6F5")
                    property("font-family", "inherit")
                    property("font-size", s(16))
                    property("font-weight", "300")
                    property("line-height", "1.5")
                    property("letter-spacing", s(0.16))
                }
            })
            }   // NoAutofillFormWrap
        }
    }
}

@Composable
private fun AboutMeTextArea(value: String, onChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(96)
            .sBorderRadius(18)
            .styleModifier {
                property("position", "relative")
                property("background", EDIT_FIELD_BG)
            }
    ) {
        GradientBorderRing(radiusPx = 18)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", s(12))
                    property("display", "flex")
                }
        ) {
            TextArea(attrs = Modifier.toAttrs {
                value(value)
                onInput { onChange(it.value) }
                attr("maxlength", BIO_MAX.toString())
                attr("placeholder", t.profilePersonalEditBioPlaceholder)
                style {
                    property("flex", "1 1 0")
                    property("min-width", "0")
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("resize", "none")
                    property("color", "#BED6F5")
                    property("font-family", "inherit")
                    property("font-size", s(16))
                    property("font-weight", "400")
                    property("line-height", "1.5")
                    property("letter-spacing", s(0.16))
                }
            })
        }
    }
}

@Composable
private fun LanguageChip(label: String, onRemove: () -> Unit) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sBorderRadius(100)
            .styleModifier {
                property("position", "relative")
                property("background", EDIT_PILL_BG)
            }
    ) {
        GradientBorderRing(radiusPx = 100)
        Row(
            modifier = Modifier
                .sGap(6)
                .styleModifier {
                    property("padding", "${s(2)} ${s(12)}")
                    property("display", "inline-flex")
                    property("align-items", "center")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(sportColors.primary)
            )
            Box(
                modifier = Modifier
                    .sSize(16)
                    .cursor(Cursor.Pointer)
                    .onClick { onRemove() },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Close, modifier = Modifier.sSize(10))
            }
        }
    }
}

// =============================================================================
// Date of birth — auto-inserts dots: 11111997 → 11.11.1997
// =============================================================================

/** Strip non-digits, cap at 8 digits, then insert dots: DD.MM.YYYY. */
internal fun maskDob(input: String): String {
    val digits = input.filter { it.isDigit() }.take(8)
    if (digits.isEmpty()) return ""
    return buildString {
        append(digits.substring(0, minOf(2, digits.length)))
        if (digits.length > 2) {
            append('.')
            append(digits.substring(2, minOf(4, digits.length)))
            if (digits.length > 4) {
                append('.')
                append(digits.substring(4))
            }
        }
    }
}

@Composable
private fun DateOfBirthField(
    value: String,
    onChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current
    Column(modifier = modifier.sGap(6)) {
        FieldLabel(t.profilePersonalEditDobLabel, required = true)
        FieldInput(
            value = value,
            placeholder = "DD.MM.YYYY",
            onChange = onChange,
            inputMode = "numeric",
        )
    }
}

// =============================================================================
// City — Nominatim autocomplete (cities only)
// =============================================================================

@Composable
private fun CityAutocompleteField(
    value: String,
    onChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    val container = LocalAppContainer.current
    var suggestions by remember { mutableStateOf<List<CityResult>>(emptyList()) }
    var suggestionsFromGoogle by remember { mutableStateOf(false) }
    var focused by remember { mutableStateOf(false) }
    // Skip the next debounced search after the user picks a suggestion — otherwise
    // setting `value` to the chosen city would immediately re-open the dropdown.
    var skipNextSearch by remember { mutableStateOf(false) }
    // Delay clearing `focused` on blur so a suggestion click lands before the
    // matches list unmounts (mousedown blurs the input, click fires after).
    var blurJob by remember { mutableStateOf<Job?>(null) }
    // See FieldInput for the bidirectional readonly trick — re-applied on
    // every blur so the next focus is gated and Chrome's autofill stays off.
    var fieldFocused by remember { mutableStateOf(false) }
    var showMapPicker by remember { mutableStateOf(false) }
    val mobile = isMobile()

    LaunchedEffect(value) {
        if (skipNextSearch) {
            skipNextSearch = false
            return@LaunchedEffect
        }
        val q = value.trim()
        if (q.length < 2) {
            suggestions = emptyList()
            return@LaunchedEffect
        }
        delay(300)
        // Google Places (cities) via the backend proxy first — the profile
        // stores only the city NAME, so no place-details follow-up and no
        // session token. Nominatim searchCities when the backend has no key
        // or is unreachable. Mirrors the mobile CityAutocompleteField.
        val geo = cachedUserCoordinates()
        val google = try {
            container.placesApi?.autocomplete(q, geo?.latitude, geo?.longitude, session = null, types = "cities")?.data
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            null
        }
        if (!google.isNullOrEmpty()) {
            // Google predictions carry no coordinates; the profile pick only
            // uses the city name, so 0/0 placeholders never leave this field.
            suggestions = google.map { CityResult(city = it.title, country = it.subtitle, lat = 0.0, lng = 0.0) }
            suggestionsFromGoogle = true
        } else {
            suggestions = searchCities(q)
            suggestionsFromGoogle = false
        }
    }

    val showMatches = focused && suggestions.isNotEmpty()

    Column(modifier = modifier.sGap(6)) {
        FieldLabel(t.profilePersonalEditCityLabel, required = true)
        // Same unified-field pattern as Languages: outer Box reserves the
        // 44px slot in flow; inner Box is absolute and can grow downward to
        // host suggestions, with a two-layer background (opaque modal surface
        // under the translucent field gradient) so nothing bleeds through
        // when the dropdown floats over neighbouring sections.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    // Mobile sheet: the expansion is IN-FLOW and pushes the
                    // form down (parity with the mobile app). Desktop keeps
                    // the absolute overlay over the sections below.
                    if (mobile) Modifier
                    else Modifier.sHeight(44).styleModifier { property("position", "relative") }
                )
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMinHeight(44)
                    .sBorderRadius(28)
                    .then(
                        // relative on mobile: GradientBorderRing inside is
                        // absolutely positioned (inset 0) and must anchor to
                        // THIS surface, not the whole sheet.
                        if (mobile) Modifier.styleModifier { property("position", "relative") }
                        else Modifier.position(Position.Absolute).zIndex(if (showMatches) 100 else 0)
                    )
                    .styleModifier {
                        if (!mobile) {
                            property("top", "0")
                            property("left", "0")
                        }
                        property("background", "$EDIT_FIELD_BG, $EDIT_MODAL_SURFACE")
                        if (focused) {
                            property("border", "0.5px solid rgba(0, 97, 255, 0.6)")
                        } else {
                            property("border", "0.5px solid transparent")
                        }
                    }
            ) {
                if (!focused) GradientBorderRing(radiusPx = 28)
                Column(modifier = Modifier.fillMaxWidth()) {
                    // Input row — 44 tall, matches FieldInput dimensions.
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(44)
                            .styleModifier {
                                property("padding", "0 ${s(12)}")
                                property("display", "flex")
                                property("align-items", "center")
                            }
                    ) {
                        NoAutofillFormWrap {
                        Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = Modifier.toAttrs {
                            value(value)
                            // Chrome ignores autocomplete="off" on heuristically-detected
                            // address fields, so we (a) feed it a non-spec token, (b) drop
                            // any "city"-like name attribute, and (c) opt out of the major
                            // password managers — together this kills the native suggestion
                            // panel so only our own dropdown shows.
                            attr("autocomplete", "off")
                            attr("autocorrect", "off")
                            attr("autocapitalize", "off")
                            attr("spellcheck", "false")
                            // Non-address-like name so Chrome's heuristic doesn't
                            // re-identify this as a city field and resurrect autofill.
                            attr("name", "arena-city-query")
                            attr("data-form-type", "other")
                            attr("data-lpignore", "true")
                            attr("data-1p-ignore", "true")
                            attr("placeholder", t.profilePersonalEditCitySearchPlaceholder)
                            if (!fieldFocused && !mobile) attr("readonly", "true")
                            onInput { onChange(it.value) }
                            onFocusIn {
                                fieldFocused = true
                                blurJob?.cancel()
                                focused = true
                            }
                            onFocusOut {
                                fieldFocused = false
                                blurJob?.cancel()
                                blurJob = scope.launch {
                                    delay(120)
                                    focused = false
                                }
                            }
                            style {
                                property("width", "100%")
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("color", "#BED6F5")
                                property("font-family", "inherit")
                                property("font-size", s(16))
                                property("font-weight", "300")
                                property("line-height", "1.5")
                                property("letter-spacing", s(0.16))
                            }
                        })
                        }   // NoAutofillFormWrap
                        // Trailing map button — opens the centre-pin picker
                        // (parity with the mobile app's city field). Mobile
                        // only: desktop picks the city by typing.
                        if (mobile) {
                            Box(
                                modifier = Modifier
                                    .sSize(32)
                                    .flexShrink(0)
                                    .sBorderRadius(100)
                                    .cursor(Cursor.Pointer)
                                    .onClick { showMapPicker = true }
                                    .styleModifier {
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    src = AppIcons.Map,
                                    modifier = Modifier.sSize(16).styleModifier {
                                        property("filter", AppColors.IconDefaultFilter)
                                    }
                                )
                            }
                        }
                    }

                    if (showMatches) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(1)
                                .sMarginBottom(6)
                                .styleModifier {
                                    property("background", "rgba(255, 255, 255, 0.06)")
                                }
                        )
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier {
                                    property("padding", "0 ${s(6)} ${s(6)}")
                                    property("max-height", "240px")
                                    property("overflow-y", "auto")
                                    property("scrollbar-width", "thin")
                                    property("scrollbar-color", "rgba(255, 255, 255, 0.15) transparent")
                                }
                        ) {
                            suggestions.forEach { c ->
                                CitySuggestionRow(
                                    city = c.city,
                                    country = c.country,
                                    query = value.trim(),
                                    onPick = {
                                        skipNextSearch = true
                                        onChange(c.city)
                                        suggestions = emptyList()
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    // Centre-pin map picker (parity with the mobile app's City field): drag
    // the map, Confirm reverse-geocodes the centre into the city name.
    if (showMapPicker) {
        var pinLat by remember { mutableStateOf<Double?>(null) }
        var pinLng by remember { mutableStateOf<Double?>(null) }
        BottomSheet(onDismiss = { showMapPicker = false }, contentGap = 12, fullHeight = true) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(12)
                    .styleModifier {
                        property("flex", "1")
                        property("min-height", "0")
                    }
            ) {
                SpanText(
                    t.eventChooseOnMap,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .color(SuggestionPrimary)
                )
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .sBorderRadius(12)
                        .styleModifier {
                            property("min-height", "0")
                            property("overflow", "hidden")
                            property("border", "0.5px solid rgba(255,255,255,0.1)")
                        }
                        .id("profile-city-map")
                ) {}
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(44)
                        .sBorderRadius(22)
                        .cursor(Cursor.Pointer)
                        .styleModifier { property("background", "#0061FF") }
                        .onClick {
                            val la = pinLat
                            val ln = pinLng
                            if (la == null || ln == null) {
                                showMapPicker = false
                            } else {
                                scope.launch {
                                    reverseGeocode(la, ln)?.city?.let { picked ->
                                        skipNextSearch = true
                                        onChange(picked)
                                        suggestions = emptyList()
                                    }
                                    showMapPicker = false
                                }
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.eventConfirmLocation,
                        modifier = Modifier
                            .sFontSize(15)
                            .fontWeight(AppTypography.SemiBold)
                            .color(Color("#FFFFFF"))
                    )
                }
                Box(modifier = Modifier.fillMaxWidth().sHeight(16))
            }
            LaunchedEffect(Unit) {
                delay(200)
                // The already-chosen city wins — the pin opens on it, not on
                // the user's location (the profile stores no coordinates, so
                // the city name is forward-geocoded on open).
                val chosen = value.trim().takeIf { it.isNotBlank() }
                    ?.let { searchCities(it).firstOrNull() }
                val geo = cachedUserCoordinates()
                val startLat = chosen?.lat ?: geo?.latitude ?: 30.0
                val startLng = chosen?.lng ?: geo?.longitude ?: 10.0
                val zoom = if (chosen != null || geo != null) 11 else 2
                LeafletMap.init("profile-city-map", startLat, startLng, zoom = zoom)
                pinLat = startLat
                pinLng = startLng
                LeafletMap.onMoveEnd { la, ln ->
                    pinLat = la
                    pinLng = ln
                }
            }
            DisposableEffect(Unit) {
                onDispose { LeafletMap.destroy() }
            }
        }
    }

}

// =============================================================================
// Languages — multi-select autocomplete with ghost-text suggestion (Figma 7694-307422)
// =============================================================================

@Composable
private fun LanguageAutocompleteField(
    selected: List<String>,
    onAdd: (String) -> Unit,
    onRemove: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    val mobile = isMobile()
    var query by remember { mutableStateOf("") }
    var focused by remember { mutableStateOf(false) }
    // Delay clearing `focused` on blur so a suggestion click lands before the
    // matches list unmounts (mousedown blurs the input, click fires after).
    var blurJob by remember { mutableStateOf<Job?>(null) }

    val matches = remember(query, selected) {
        Languages.search(query, exclude = selected.toSet(), limit = 8)
    }
    val showMatches = focused && query.trim().isNotEmpty() && matches.isNotEmpty()

    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        FieldLabel(t.profilePersonalEditLanguagesLabel, required = true)

        if (selected.isNotEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(6)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("row-gap", s(6))
                    }
            ) {
                selected.forEach { lang ->
                    LanguageChip(lang) { onRemove(lang) }
                }
            }
        }

        // Outer Box reserves a fixed 73px slot in flow. The actual visible
        // field is absolutely positioned inside it and can grow downward to
        // host the matches list — so it always looks like ONE unified field,
        // but the modal layout never shifts and the expansion floats over the
        // sections below.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    // Mobile sheet: in-flow expansion (parity with the app).
                    if (mobile) Modifier
                    else Modifier.sMinHeight(73).styleModifier { property("position", "relative") }
                )
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMinHeight(73)
                    .sBorderRadius(18)
                    .then(
                        // relative on mobile — see the City field note.
                        if (mobile) Modifier.styleModifier { property("position", "relative") }
                        else Modifier.position(Position.Absolute).zIndex(if (showMatches) 100 else 0)
                    )
                    .styleModifier {
                        if (!mobile) {
                            property("top", "0")
                            property("left", "0")
                        }
                        // Two stacked layers (top → bottom): the translucent field
                        // gradient sits over the opaque modal surface, so once the
                        // dropdown floats over the About-me section below, nothing
                        // bleeds through.
                        property("background", "$EDIT_FIELD_BG, $EDIT_MODAL_SURFACE")
                        if (focused) {
                            property("border", "0.5px solid rgba(0, 97, 255, 0.6)")
                        } else {
                            property("border", "0.5px solid transparent")
                        }
                    }
            ) {
                if (!focused) GradientBorderRing(radiusPx = 18)
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("padding", "${s(12)} ${s(12)}")
                        }
                ) {
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = Modifier.toAttrs {
                        value(query)
                        attr("autocomplete", "off")
                        attr("placeholder", if (selected.isEmpty()) t.profilePersonalEditTypeLanguagePlaceholder else t.profilePersonalEditAddLanguagePlaceholder)
                        onInput { query = it.value }
                        onFocusIn {
                            blurJob?.cancel()
                            focused = true
                        }
                        onFocusOut {
                            blurJob?.cancel()
                            blurJob = scope.launch {
                                delay(120)
                                focused = false
                            }
                        }
                        onKeyDown { ev ->
                            if (ev.key == "Enter") {
                                ev.preventDefault()
                                matches.firstOrNull()?.let {
                                    onAdd(it)
                                    query = ""
                                }
                            }
                        }
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("color", "#BED6F5")
                            property("font-family", "inherit")
                            property("font-size", s(16))
                            property("font-weight", "400")
                            property("line-height", "1.5")
                            property("letter-spacing", s(0.16))
                            property("width", "100%")
                            property("min-width", s(120))
                            property("caret-color", "#0061FF")
                        }
                    })

                    if (showMatches) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(1)
                                .sMarginTop(10)
                                .sMarginBottom(6)
                                .styleModifier {
                                    property("background", "rgba(255, 255, 255, 0.06)")
                                }
                        )
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier {
                                    property("max-height", "200px")
                                    property("overflow-y", "auto")
                                    property("scrollbar-width", "thin")
                                    property("scrollbar-color", "rgba(255, 255, 255, 0.15) transparent")
                                }
                        ) {
                            matches.forEach { lang ->
                                LanguageSuggestionRow(
                                    lang = lang,
                                    query = query.trim(),
                                    onPick = {
                                        onAdd(lang)
                                        query = ""
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// =============================================================================
// Suggestion rows (Figma 7694:307353) — the matched substring renders in primary
// blue (#BED6F5), the surrounding text in muted grey so each row reads as an
// inline ghost-completion. Used by both Language and City autocompletes.
// =============================================================================
// MatchedText + suggestion colors moved to ui/components/widgets/MatchedText.kt
// — shared with the address autocompletes (create event / club, Settings).

@Composable
private fun LanguageSuggestionRow(
    lang: String,
    query: String,
    onPick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 6)
            .sBorderRadius(8)
            .cursor(Cursor.Pointer)
            .onClick { onPick() }
    ) {
        MatchedText(text = lang, query = query)
    }
}

@Composable
private fun CitySuggestionRow(
    city: String,
    country: String?,
    query: String,
    onPick: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 6)
            .sBorderRadius(8)
            .cursor(Cursor.Pointer)
            .onClick { onPick() }
    ) {
        MatchedText(text = city, query = query)
        if (!country.isNullOrBlank()) {
            SpanText(
                country,
                modifier = Modifier
                    .sFontSize(12)
                    .sLineHeight(16)
                    .color(SuggestionMuted)
                    .sMarginTop(2)
            )
        }
    }
}
